import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Search } from 'lucide-react'
import { cn } from '../../../lib/utils'
import ExercisePlaceholder from './ExercisePlaceholder'

const FILTER_GROUPS = ['all', 'chest', 'back', 'shoulders', 'biceps', 'triceps', 'quads', 'hamstrings', 'core']

export default function ExercisePicker({ exercises, excludeIds = [], onPick, onClose }) {
  const { t } = useTranslation()
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')

  const excludeSet = useMemo(() => new Set(excludeIds), [excludeIds])

  const filtered = useMemo(() => {
    return exercises.filter((e) => {
      if (excludeSet.has(e.id)) return false
      if (filter !== 'all' && e.muscle_group !== filter) return false
      if (search && !e.name.toLowerCase().includes(search.toLowerCase())) return false
      return true
    })
  }, [exercises, filter, search, excludeSet])

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-card w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-xl max-h-[85vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="picker-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 border-b border-border flex items-center justify-between gap-2">
          <h2 id="picker-title" className="text-sm font-semibold text-foreground">
            {t('workout.picker.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('workout.picker.close')}
            className="text-muted-foreground hover:text-foreground p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-3 space-y-3 border-b border-border">
          <div className="relative">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('workout.exercises.searchPlaceholder')}
              className="h-10 w-full ps-10 pe-3 rounded-md border border-input bg-background text-foreground text-base sm:text-sm focus:outline-none"
              autoFocus
            />
          </div>
          <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1">
            {FILTER_GROUPS.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setFilter(g)}
                className={cn(
                  'h-8 px-3 rounded-full text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0',
                  filter === g
                    ? 'bg-app-primary-500 text-white'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {g === 'all' ? t('workout.exercises.filterAll') : t(`workout.muscleGroup.${g}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              {exercises.length === 0
                ? t('workout.picker.empty')
                : t('workout.exercises.noResults')}
            </p>
          ) : (
            <ul className="space-y-1">
              {filtered.map((e) => (
                <li key={e.id}>
                  <button
                    type="button"
                    onClick={() => onPick(e.id)}
                    className="w-full text-start p-3 rounded-lg hover:bg-accent transition-colors flex items-center gap-3"
                  >
                    {e.photoUrl ? (
                      <img
                        src={e.photoUrl}
                        alt=""
                        className="w-10 h-10 rounded-md object-cover bg-muted flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0">
                        <ExercisePlaceholder
                          muscleGroup={e.muscle_group}
                          iconClassName="w-5 h-5"
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{e.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {t(`workout.muscleGroup.${e.muscle_group}`)} · {t(`workout.equipment.${e.equipment_type}`)}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
